/* Audio Everywhere — cross-section utilities.
   Loaded with defer, so DOM is available on execute. */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

(function () {
  'use strict';

  /* ─── Toast Manager ──────────────────────────────────────
     Slide-in from top with spring entry, fade-out on exit.
     Multiple toasts stack. ESC dismisses active toast. */
  const Toast = (() => {
    const stack = document.getElementById('toast-stack');
    if (!stack) return { show: () => {} };

    const DURATION_VISIBLE = 3000;
    const ANIMATION_DURATION = 240;

    function show({ title, subtitle, onClick }) {
      const toast = document.createElement('div');
      toast.className = 'toast';
      toast.setAttribute('role', 'status');

      const titleSafe = String(title || '').replace(/[<>&"']/g, (c) => ({
        '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
      }[c]));
      const subSafe = subtitle
        ? String(subtitle).replace(/[<>&"']/g, (c) => ({
            '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
          }[c]))
        : '';

      toast.innerHTML =
        '<svg class="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<polyline points="20 6 9 17 4 12"/></svg>' +
        '<div class="toast__content">' +
        '<div class="toast__title">' + titleSafe + '</div>' +
        (subSafe ? '<div class="toast__subtitle">' + subSafe + '</div>' : '') +
        '</div>';

      if (onClick) {
        toast.addEventListener('click', () => {
          clearTimeout(dismissTimer);
          dismiss(toast);
          onClick();
        });
      }

      stack.appendChild(toast);
      void toast.offsetWidth;
      requestAnimationFrame(() => toast.classList.add('toast--visible'));

      function onEsc(e) {
        if (e.key === 'Escape' && toast.parentElement) {
          clearTimeout(dismissTimer);
          dismiss(toast);
        }
      }
      document.addEventListener('keydown', onEsc);
      toast._onEsc = onEsc;

      const dismissTimer = setTimeout(() => {
        document.removeEventListener('keydown', onEsc);
        dismiss(toast);
      }, DURATION_VISIBLE);

      return toast;
    }

    function dismiss(toast) {
      if (!toast || !toast.parentElement) return;
      if (toast._onEsc) {
        document.removeEventListener('keydown', toast._onEsc);
        toast._onEsc = null;
      }
      toast.classList.remove('toast--visible');
      toast.classList.add('toast--leaving');
      setTimeout(() => {
        if (toast.parentElement) toast.parentElement.removeChild(toast);
      }, ANIMATION_DURATION);
    }

    return { show };
  })();
  window.AETOAST = Toast;

  /* ─── Cart Counter Smooth Update ─────────────────────────
     Animates from old to new count over ~280ms. Skips animation
     for reduced-motion or large jumps (>5). */
  function updateCartCounter(newCount) {
    const counter = document.querySelector('.site-header__cart-count');
    if (!counter) return;

    const oldCount = parseInt(counter.textContent || '0', 10) || 0;
    const target = parseInt(newCount, 10) || 0;

    counter.classList.toggle('is-empty', target === 0);
    if (oldCount === target) return;

    if (target === 0) {
      counter.textContent = '0';
      return;
    }

    if (reduceMotion || Math.abs(target - oldCount) > 5) {
      counter.textContent = target;
      return;
    }

    const start = performance.now();
    const duration = 280;
    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      counter.textContent = Math.round(oldCount + (target - oldCount) * eased);
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        counter.textContent = target;
      }
    }
    requestAnimationFrame(tick);
  }
  window.AECARTCOUNTER = updateCartCounter;

  /* ─── AJAX Add-to-Cart ───────────────────────────────────
     Any form[action*="/cart/add"] upgrades to async.
     On success: animates counter, shows toast linked to cart-drawer. */
  function bindAjaxAddToCart() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        if (!btn) return;

        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = btn.dataset.addingText || window.AETHEME.strings.cart.adding;

        try {
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form)
          });
          if (!addRes.ok) throw new Error('Add to cart failed: ' + addRes.status);
          const addedItem = await addRes.json();

          const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then((r) => r.json());

          updateCartCounter(cart.item_count);

          const strings = (window.AETHEME && window.AETHEME.strings && window.AETHEME.strings.cart) || {};
          const toastTitle = addedItem.product_title || strings.added_to_cart_title || 'Added to cart';
          Toast.show({
            title: toastTitle,
            subtitle: strings.added_to_cart_subtitle || '',
            onClick: () => {
              const drawer = document.querySelector('cart-drawer');
              if (drawer && typeof drawer.open === 'function') {
                drawer.open();
              } else {
                window.location.href = '/cart';
              }
            }
          });

          btn.textContent = btn.dataset.addedText || window.AETHEME.strings.cart.added;
          setTimeout(() => {
            btn.textContent = original;
            btn.disabled = false;
          }, 800);
        } catch (err) {
          if (window.AETHEME?.debug) console.error('[add-to-cart]', err);
          btn.textContent = original;
          btn.disabled = false;
        }
      });
    });
  }

  /* ─── Search drawer ──────────────────────────────────────
     Click on the search icon opens an overlay drawer with an
     auto-focused input. ESC, the close button, or overlay-click
     closes it. Cmd/Ctrl+K opens it as a power-user shortcut. */
  function bindSearchDrawer() {
    const toggle = document.querySelector('[data-search-toggle]');
    const drawer = document.querySelector('[data-search-drawer]');
    if (!toggle || !drawer) return;

    const input = drawer.querySelector('input[type="search"]');
    const closers = drawer.querySelectorAll('[data-search-close]');

    function open() {
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      setTimeout(() => { if (input) input.focus(); }, 80);
    }

    function close() {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', open);
    closers.forEach((c) => c.addEventListener('click', close));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        e.preventDefault();
        close();
        toggle.focus();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        const target = e.target;
        if (target && /input|textarea|select/i.test(target.tagName)) return;
        e.preventDefault();
        open();
      }
    });
  }

  /* ─── Scroll Reveal ──────────────────────────────────────
     Quiet fade + 8px translateY on elements with .reveal.
     One-shot per element; respects prefers-reduced-motion. */
  function bindScrollReveal() {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0.1 }
    );

    els.forEach((el) => io.observe(el));
  }

  /* Sticky Mobile CTA: statisch sichtbar via CSS — kein JS nötig. */

  function init() {
    bindAjaxAddToCart();
    bindSearchDrawer();
    bindScrollReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ─── Hero Fullscreen Slideshow ──────────────────────────
   Slow cross-fade between background slides. 2000ms fade,
   7000ms interval. Pauses on hover, restarts on leave.
   Respects prefers-reduced-motion (shows first slide only). */
(function () {
  const hero = document.querySelector('.hero--fullscreen');
  if (!hero) return;

  const slides = hero.querySelectorAll('.hero__slide');
  const indicators = hero.querySelectorAll('.hero__indicator');
  if (slides.length <= 1) return;

  const INTERVAL = 7000;
  if (reduceMotion) return;

  let current = 0;
  let timer = null;

  function goTo(index) {
    slides[current].classList.remove('is-active');
    if (indicators[current]) {
      indicators[current].classList.remove('is-active');
      indicators[current].setAttribute('aria-selected', 'false');
    }
    current = (index + slides.length) % slides.length;
    slides[current].classList.add('is-active');
    if (indicators[current]) {
      indicators[current].classList.add('is-active');
      indicators[current].setAttribute('aria-selected', 'true');
    }
  }

  function start() {
    stop();
    timer = window.setInterval(function () { goTo(current + 1); }, INTERVAL);
  }

  function stop() {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  }

  indicators.forEach(function (btn, i) {
    btn.addEventListener('click', function () {
      stop();
      goTo(i);
      start();
    });
  });

  hero.addEventListener('mouseenter', stop);
  hero.addEventListener('mouseleave', start);

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stop(); else start();
  });

  start();
})();

/* ─── Site Header — Smart Hide on Scroll ──────────────────
   Header is permanently position: fixed (CSS). This script only
   toggles state-classes — never the position property — so there's
   no reflow flash at the threshold. (Audit Fix 5.)
     A) scrollY < 100        → neither class (transparent over hero)
     B) scrollY ≥ 100 + UP   → --scrolled (cream bar visible)
     C) scrollY ≥ 100 + DOWN → --scrolled + --hidden (slid up)
   SCROLL_DELTA = 5 absorbs jittery touchpad noise. */
(function () {
  function initSmartHideNav() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    const SCROLL_THRESHOLD = 100;
    const SCROLL_DELTA = 5;
    let lastScrollY = window.scrollY;
    let ticking = false;

    function update() {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY;

      if (Math.abs(scrollDelta) < SCROLL_DELTA) {
        ticking = false;
        return;
      }

      if (currentScrollY < SCROLL_THRESHOLD) {
        header.classList.remove('site-header--scrolled');
        header.classList.remove('site-header--hidden');
      } else {
        header.classList.add('site-header--scrolled');
        if (scrollDelta > 0) {
          header.classList.add('site-header--hidden');
        } else {
          header.classList.remove('site-header--hidden');
        }
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmartHideNav);
  } else {
    initSmartHideNav();
  }
})();

/* ─── /cart — auto-submit on quantity change ─────────────────
   600ms debounce so rapid arrow-key spam coalesces into one POST. */
(function () {
  function initCartQuantityAutoSubmit() {
    var form = document.querySelector('.cart-items__form');
    if (!form) return;
    var inputs = form.querySelectorAll('[data-cart-quantity]');
    if (!inputs.length) return;

    var debounceTimer;
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          form.submit();
        }, 600);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartQuantityAutoSubmit);
  } else {
    initCartQuantityAutoSubmit();
  }
})();

/* ─── PDP — Gallery thumbs + Qty stepper + Variant sync ──────
   Was inline in sections/main-product.liquid (K2 extraction).
   Idempotent: data-pdp-bound flag prevents double-binding if
   the section ever re-renders. Out-of-stock label comes from
   window.AETHEME.strings.product.out_of_stock (locale bridge). */
(function () {
  function initProductPage(root) {
    if (!root || root.dataset.pdpBound === 'true') return;
    root.dataset.pdpBound = 'true';

    var thumbs = root.querySelectorAll('.product-gallery__thumb');
    var slides = root.querySelectorAll('.product-gallery__slide');
    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-media-id');
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === btn); });
        slides.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-media-id') === id); });
      });
    });

    var qtyInput = root.querySelector('.product-page__qty input');
    var qtyBtns = root.querySelectorAll('[data-qty-step]');
    qtyBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        if (!qtyInput) return;
        var step = parseInt(b.getAttribute('data-qty-step'), 10) || 0;
        var val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, val + step);
      });
    });

    var dataEl = root.querySelector('[data-product-json]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!data || !data.variants || !data.options) return;

    var form = root.querySelector('.product-page__form');
    if (!form) return;
    var hiddenId = form.querySelector('input[name="id"]');
    var optionInputs = form.querySelectorAll('input[name^="options["]');
    var priceBox = root.querySelector('.product-page__price');
    var addBtn = form.querySelector('.product-page__add-to-cart');
    var btnLabelAvailable = addBtn ? addBtn.textContent.trim() : '';
    var productStrings = (window.AETHEME && window.AETHEME.strings && window.AETHEME.strings.product) || {};
    var btnLabelUnavailable = productStrings.out_of_stock || 'Out of stock';

    function moneyFormat(cents) {
      var amount = (cents / 100).toFixed(2).replace('.', ',');
      return amount + ' €';
    }

    function getSelectedOptions() {
      var sel = {};
      optionInputs.forEach(function (inp) {
        if (inp.checked) {
          var m = inp.name.match(/options\[(.+)\]/);
          if (m) sel[m[1]] = inp.value;
        }
      });
      return sel;
    }

    function findVariant(sel) {
      return data.variants.find(function (v) {
        return data.options.every(function (optName, i) {
          return sel[optName] === v.options[i];
        });
      });
    }

    function updateGallery(mediaId) {
      if (!mediaId) return;
      var idStr = String(mediaId);
      slides.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-media-id') === idStr); });
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-media-id') === idStr); });
    }

    function syncVariant() {
      var sel = getSelectedOptions();
      var v = findVariant(sel);
      if (!v) return;

      if (hiddenId) hiddenId.value = v.id;

      if (priceBox) {
        var html;
        if (v.compare_at_price && v.compare_at_price > v.price) {
          html = '<span class="product-page__price-sale">' + moneyFormat(v.price) + '</span>'
               + '<s class="product-page__price-compare">' + moneyFormat(v.compare_at_price) + '</s>';
        } else {
          html = '<span class="product-page__price-regular">' + moneyFormat(v.price) + '</span>';
        }
        priceBox.innerHTML = html;
      }

      // easyCredit: Ratenkauf-Widget-Betrag + Sichtbarkeit im Gleichschritt mit dem Variantenpreis halten.
      // amount = Euro mit Punkt-Dezimal (Komponente erwartet KEIN deutsches Komma → nicht moneyFormat nutzen).
      var ecWrap = root.querySelector('[data-ec-wrap]');
      var ecWidget = ecWrap && ecWrap.querySelector('easycredit-widget');
      var ecInRange = v.price >= 20000 && v.price <= 1000000;
      if (ecWidget) ecWidget.setAttribute('amount', (v.price / 100).toFixed(2));
      if (ecWrap) ecWrap.hidden = !ecInRange;
      // Express-Kauf-Button: variantId + Verfügbarkeit im Gleichschritt (Sichtbarkeit via CSS: data-avail).
      var ecExpress = root.querySelector('[data-ec-express]');
      if (ecExpress) {
        ecExpress.setAttribute('data-variant-id', v.id);
        ecExpress.setAttribute('data-avail', (ecInRange && v.available) ? 'true' : 'false');
      }

      // Klarna: preisunabhängig (kein Show/Hide). data-purchase-amount = CENT (= v.price DIREKT, NICHT /100!).
      var klarna = root.querySelector('klarna-placement');
      if (klarna) {
        klarna.setAttribute('data-purchase-amount', v.price);
        if (window.Klarna && window.Klarna.OnsiteMessaging && typeof window.Klarna.OnsiteMessaging.refresh === 'function') {
          window.Klarna.OnsiteMessaging.refresh();
        }
      }

      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? btnLabelAvailable : btnLabelUnavailable;
      }

      if (v.featured_media && v.featured_media.id) {
        updateGallery(v.featured_media.id);
      }

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    optionInputs.forEach(function (inp) {
      inp.addEventListener('change', syncVariant);
    });

    // easyCredit Express-Kauf: dynamischer Formular-POST (kein fetch -> 302-Redirect auf die
    // easyCredit-Payment-Page laeuft nativ). Einmalig gebunden; data-variant-id/data-avail haelt syncVariant aktuell.
    var ecExpressBtn = root.querySelector('[data-ec-express]');
    if (ecExpressBtn) ecExpressBtn.addEventListener('click', function () {
      if (ecExpressBtn.getAttribute('data-busy') === 'true') return; // Doppelklick-Guard
      var hub = (ecExpressBtn.getAttribute('data-hub-url') || '').replace(/\/+$/, '');
      var variantId = ecExpressBtn.getAttribute('data-variant-id');
      if (!hub || !variantId) return;
      ecExpressBtn.setAttribute('data-busy', 'true');
      ecExpressBtn.disabled = true;
      var form = document.createElement('form');
      form.method = 'post';
      form.action = hub + '/api/easycredit/express/start';
      form.style.display = 'none';
      var fields = { variantId: variantId, quantity: '1' };
      Object.keys(fields).forEach(function (name) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = fields[name];
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    });
  }

  function init() {
    document.querySelectorAll('.product-page').forEach(initProductPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ─── PDP — Price-Offer Modal ────────────────────────────────
   Was inline in sections/main-product.liquid (K2 extraction).
   Locale strings + hub URL come from data-* attrs on [data-offer-modal]
   so the IIFE stays generic. Used-product gate is enforced by Liquid:
   when [data-offer-modal] is missing, this IIFE no-ops. */
(function () {
  function initOfferModal() {
    var modal = document.querySelector('[data-offer-modal]');
    var trigger = document.querySelector('[data-offer-open]');
    var form = document.querySelector('[data-offer-form]');
    var statusEl = document.querySelector('[data-offer-status]');
    if (!modal || !trigger || !form || !statusEl) return;
    if (modal.dataset.offerBound === 'true') return;
    modal.dataset.offerBound = 'true';

    var HUB = modal.dataset.hubUrl || '';
    var MSG = {
      sending: modal.dataset.msgSending || '',
      ok: modal.dataset.msgOk || '',
      low: modal.dataset.msgLow || '',
      err: modal.dataset.msgErr || '',
      retry: modal.dataset.msgRetry || ''
    };

    function open() {
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      statusEl.textContent = '';
      statusEl.className = 'offer-form__status';
    }

    trigger.addEventListener('click', open);
    document.querySelectorAll('[data-offer-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') close();
    });

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      var btn = form.querySelector('.offer-form__submit');
      if (btn) btn.disabled = true;
      statusEl.textContent = MSG.sending;
      statusEl.className = 'offer-form__status';

      var fd = new FormData(form);
      var payload = {};
      fd.forEach(function (v, k) { payload[k] = v; });
      payload.offerAmount = Number(payload.offerAmount);

      try {
        var res = await fetch(HUB + '/api/price-offer/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        var data = await res.json().catch(function () { return {}; });
        if (!res.ok || !data.ok) {
          throw new Error(data.error || ('HTTP ' + res.status));
        }
        statusEl.textContent = data.status === 'auto-rejected' ? MSG.low : MSG.ok;
        statusEl.className = 'offer-form__status ok';
        form.reset();
        setTimeout(close, 4500);
      } catch (err) {
        statusEl.textContent = MSG.err + ': ' + err.message + MSG.retry;
        statusEl.className = 'offer-form__status err';
      } finally {
        if (btn) btn.disabled = false;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOfferModal);
  } else {
    initOfferModal();
  }
})();

/* ─── PDP — Hub-Widget-Fallbacks (Redesign 2026-07-11) ───────
   Elemente mit [data-ae-fallback-for="<selector>"] zeigen sich,
   wenn das Ziel-Widget (delivery-estimate / warranty-info) nach
   einer Grace-Period weiterhin [hidden] ist — z. B. Hub show:false
   oder Fetch-Fehler. Wird das Widget später sichtbar (Varianten-
   Wechsel), blendet der MutationObserver den Fallback wieder aus.
   Idempotent via data-fallback-bound. Widget-Contract unangetastet. */
(function () {
  var GRACE_MS = 4000; /* widget.js-Timeout ist 3 s — 1 s Puffer */

  function bindFallback(fb) {
    if (fb.dataset.fallbackBound === 'true') return;
    fb.dataset.fallbackBound = 'true';

    var sel = fb.getAttribute('data-ae-fallback-for');
    if (!sel) return;
    var target;
    try { target = document.querySelector(sel); } catch (e) { target = null; }

    if (!target) {
      fb.removeAttribute('hidden');
      return;
    }

    function sync() {
      if (!target.hasAttribute('hidden')) {
        fb.setAttribute('hidden', '');
        return;
      }
      if (fb.dataset.graceDone !== 'true') return;
      /* Lieferzeit-Fallback nie auf unkaufbaren Varianten zeigen — der Hub
         antwortet dort bewusst 204/hidden. Gilt nur für .ae-de--fallback
         (Varianten-Wechsel-Fall); der Garantie-Fallback bleibt unberührt. */
      if (fb.classList.contains('ae-de--fallback')) {
        var buyBtn = document.querySelector('.product-page__add-to-cart');
        if (buyBtn && buyBtn.disabled) {
          fb.setAttribute('hidden', '');
          return;
        }
      }
      fb.removeAttribute('hidden');
    }

    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(target, { attributes: true, attributeFilter: ['hidden'] });
    }

    setTimeout(function () {
      fb.dataset.graceDone = 'true';
      sync();
    }, GRACE_MS);
  }

  function init() {
    document.querySelectorAll('[data-ae-fallback-for]').forEach(bindFallback);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ─── Beratungs-Formular — Produkt-Prefill via ?product= ─────
   Der Termin-Link der PDP-Trust-Karte verweist auf
   /pages/beratung?product=<handle>. Dieses IIFE befüllt das
   Nachrichtenfeld (nur wenn leer) und das versteckte Produktfeld.
   Label kommt lokalisiert per data-prefill-label (K2: keine
   hartkodierten Strings). Idempotent via data-prefill-bound. */
(function () {
  function initBeratungPrefill() {
    var root = document.querySelector('[data-beratung-prefill]');
    if (!root || root.dataset.prefillBound === 'true') return;
    root.dataset.prefillBound = 'true';

    var param = '';
    try {
      param = new URLSearchParams(window.location.search).get('product') || '';
    } catch (e) { return; }
    param = param.replace(/[^\wÀ-ɏ-]/g, '').slice(0, 120);
    if (!param) return;

    var label = root.dataset.prefillLabel || '';
    var msg = document.getElementById('BeratungMessage');
    if (msg && !msg.value) {
      msg.value = (label ? label + ' ' : '') + param;
    }
    var hidden = document.getElementById('BeratungProduct');
    if (hidden) hidden.value = param;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBeratungPrefill);
  } else {
    initBeratungPrefill();
  }
})();

